<template>
  <sc-render :fields="fields" :empty-message="$t('NO_DATA_SOURCE_EXIST')">
    <div
      class="locations-list-component"
      :class="[themeClass, {'image-sticked': isImageSticked}]"
    >
      <sc-text
        class="locations-list-title"
        :class="{'display-mode': !isEditMode}"
        :field="headerTitle || {}"
      />
      <div
        class="locations-list-container"
        ref="locationsListContainer"
      >
        <location-image
          :location="selectedLocation"
          :theme-class="themeClass"
          :is-image-sticked="isImageSticked"
          ref="locationImage"
        />
        <div
          class="locations-list-column"
          :style="{'--list-top-padding': listTopPadding}"
        >
          <ul class="locations-list">
            <li
              class="locations-list__item"
              :class="{active:location.isLinkActive}"
              v-for="(location, index) in locationLinks"
              :key="index"
              ref="locationsListItems"
            >
              <sh-link :to="location.to">
                {{ location.title }}
              </sh-link>
            </li>
          </ul>
          <sc-text
            v-if="showFooterTitle"
            class="locations-footer-title"
            :field="footerTitle || {}"
          />
          <sc-link
            v-if="showFooterCta"
            class="locations-footer-link"
            :field="cta || {}"
          />
        </div>
      </div>
    </div>
  </sc-render>
</template>

<script>
import debounce from 'debounce'
import { createNamespacedHelpers } from 'vuex'
import { Text as ScText, Link as ScLink } from '@sitecore-jss/sitecore-jss-vue'
import designTokens from '@sohohouse/design-system/dist/tokens/tokens.json'
import get from '~/helpers/get'
import mapFields from '~/helpers/map-fields'
import ThemeMixin from '~/mixins/theme'
import ShLink from '~/components/sh-link'
import ScRender from '../sc-render'
import LocationImage from './location-image'

const { mapGetters: mapJssGetters } = createNamespacedHelpers('jss')

const DEFAULT_OFFSET = 0
const DESKTOP_OFFSET = 0
const INITIAL_SCROLL_STEP = 42
const KEYS = { ARROW_UP: 'ArrowUp', ARROW_DOWN: 'ArrowDown' }

export default {
  name: 'LocationsList',

  components: {
    ScRender,
    ScText,
    ScLink,
    ShLink,
    LocationImage
  },

  props: {
    fields: {
      type: Object,
      default: () => ({})
    }
  },

  mixins: [ThemeMixin],

  data () {
    return {
      selectedLocation: null,
      isImageSticked: false,
      listTopPadding: designTokens['space-080'],
      scrollStep: INITIAL_SCROLL_STEP
    }
  },

  computed: {
    ...mapJssGetters(['isEditMode']),
    ...mapFields({
      locations: 'locations',
      headerTitle: 'headerTitle',
      footerTitle: 'footerTitle',
      footerTitleValue: 'footerTitle.value',
      cta: 'cta.value',
      ctaHref: 'cta.value.href'
    }),

    locationLinks () {
      return this.locations && this.locations.map(location => ({
        ...location,
        isLinkActive: location === this.selectedLocation,
        to: get(location, 'link', ''),
        title: get(location, 'title.value', '')
      }))
    },

    stickyOffset () {
      return this.$device.isMobileOrTablet ? DEFAULT_OFFSET : DESKTOP_OFFSET
    },

    themeClass () {
      return `theme-${this.theme.color}`.toLowerCase()
    },

    showFooterTitle () {
      return this.isEditMode || this.footerTitleValue
    },

    showFooterCta () {
      return this.isEditMode || this.ctaHref
    }
  },

  methods: {
    setListTopPadding () {
      if (!this.$device.isMobileOrTablet) {
        this.listTopPadding = designTokens['space-080']
        return
      }

      // dynamically handle the list top padding to prevent the case
      // when device orientation is changed and an active list item
      // becomes hidden behind the fixed image
      const locationImageBox = this.$refs.locationImage.$el.getBoundingClientRect()

      if (this.$device.isMobileOrTablet && this.isImageSticked) {
        this.listTopPadding = `${locationImageBox.bottom - locationImageBox.top}px`
      } else {
        this.listTopPadding = 0
      }
    },

    setIsImageSticked () {
      // the image becomes fixed when the list container goes beyond the top edge of a viewport
      // otherwise, the page content is being scrolled in a regular way
      const locationsListContainerBox = this.$refs.locationsListContainer.getBoundingClientRect()

      this.isImageSticked = locationsListContainerBox.top - this.stickyOffset < 0
      this.setListTopPadding()
    },

    setScrollStep () {
      const locationsListContainerBox = this.$refs.locationsListContainer.getBoundingClientRect()
      const locationsListHeight = locationsListContainerBox.bottom - locationsListContainerBox.top
      const locationsLength = this.locations.length

      // modifies the scroll step on desktop to allow all locations to be highlighted on scroll
      // otherwise there could be cases when the page scrolled to the end
      // and some items can not be highlighted
      const velocityModifier = 1 - (window.innerHeight / document.body.scrollHeight)

      if (this.$device.isMobileOrTablet) {
        this.scrollStep = INITIAL_SCROLL_STEP
      } else {
        this.scrollStep = (locationsListHeight / locationsLength) * velocityModifier
      }
    },

    onScroll () {
      // depending on the scroll step and the list container position
      // an active location index is calculated
      const locationsListContainerBox = this.$refs.locationsListContainer.getBoundingClientRect()
      const scrolledBy = locationsListContainerBox.top + this.scrollStep
      const locationIndex = -Math.floor((scrolledBy - this.stickyOffset) / this.scrollStep)

      // when the index is out of bounds set the first or the last item
      // otherwise, just use calculated index to set the item
      if (locationIndex < 0 || Number.isNaN(locationIndex)) {
        const index = 0
        this.selectedLocation = this.locations[index]
      } else if (locationIndex >= this.locations.length) {
        this.selectedLocation = this.locations[this.locations.length - 1]
      } else {
        this.selectedLocation = this.locations[locationIndex]
      }

      // while on desktops the last item remains selected at the end of the page
      // on mobiles we need to unset it
      if (this.$device.isMobileOrTablet && locationIndex >= this.locations.length) {
        this.selectedLocation = null
      }

      this.setIsImageSticked()
    },

    onResize () {
      this.setIsImageSticked()
      this.setScrollStep()
    },

    scrollPage (scrollStep) {
      const options = {
        left: 0,
        top: 0,
        behavior: 'smooth'
      }

      window.scrollBy({ ...options, top: scrollStep })
    },

    onWheel (event) {
      if (!event) return
      if (this.$device.isMobileOrTablet) return

      const { deltaY } = event

      // we're bypassing low deltas
      if (Math.abs(deltaY) < this.scrollStep) return

      event.preventDefault()

      if (deltaY > 0) {
        this.scrollPage(this.scrollStep)
      }

      if (deltaY < 0) {
        this.scrollPage(-this.scrollStep)
      }
    },

    onKeydown (event) {
      if (event.key === KEYS.ARROW_DOWN) {
        event.preventDefault()
        this.scrollPage(this.scrollStep)
      }

      if (event.key === KEYS.ARROW_UP) {
        event.preventDefault()
        this.scrollPage(-this.scrollStep)
      }
    }
  },

  mounted () {
    if (!this.locations || this.locations.length < 1) {
      this.selectedLocation = null
      return
    }

    const index = 0
    this.selectedLocation = this.locations[index]

    this.debouncedOnResize = debounce(this.onResize, 200)

    window.addEventListener('scroll', this.onScroll)
    window.addEventListener('resize', this.debouncedOnResize)
    window.addEventListener('wheel', this.onWheel, { passive: false })
    window.addEventListener('keydown', this.onKeydown)

    this.onResize()
    this.onScroll()
  },

  beforeDestroy () {
    window.removeEventListener('scroll', this.onScroll)
    window.removeEventListener('resize', this.debouncedOnResize)
    window.removeEventListener('wheel', this.onWheel)
    window.removeEventListener('keydown', this.onKeydown)
  }
}
</script>

<style lang="scss" scoped>
  // the values are set in accordance with the visual design
  $tablet-title-width: 50%;
  $desktop-title-width: 24%;
  $desktop-list-column-left-margin: 375px;
  $tablet-list-container-left-margin: 10%;
  $desktop-list-container-left-margin: 30%;
  $desktop-component-bottom-margin: 245px;
  $active-item-transition-duration: .15s;

  .locations-list-component {
    position: relative;
    margin-bottom: $space-090;

    @include tablet {
      margin-bottom: $desktop-component-bottom-margin;
    }
  }

  .locations-list-title {
    display: block;
    margin-top: $space-060;
    padding: 0 $space-050;
    @include font('080');

    @include tablet {
      @include reset;
      position: absolute;
      left: $space-060;
      top: $space-060;
      width: $tablet-title-width;
      @include font('060');
    }

    @include desktop {
      @include font('080');
      width: $desktop-title-width;
    }
  }

  .locations-list-container {
    position: relative;
    overflow: hidden;

    @include tablet {
      display: flex;
      margin-left: $tablet-list-container-left-margin;
    }

    @include desktop {
      margin-left: $desktop-list-container-left-margin;
    }
  }

  .image-sticked {
    .locations-list-title {
      @include tablet {
        &.display-mode {
          position: fixed;
          right: 0;
        }
      }
    }
  }

  .locations-list-column {
    /* the css variable is used to bypass the specificity of the inline styles */
    padding-top: var(--list-top-padding);
    @include tablet {
      @include inset-space(0);
      padding-left: $space-100;
      padding-top: $space-080;
      padding-bottom: $space-015;
      margin-left: $desktop-list-column-left-margin;
    }
  }

  .locations-list {
    @include reset;
    @include stack-space($space-090);

    @include tablet {
      @include stack-space($space-110);
    }

    &__item {
      list-style: none;
      @include stack-space($space-040);
      padding: 0 $space-050;

      @include tablet {
        @include inset-space(0);
      }

      a, span {
        transition: color $active-item-transition-duration;
        color: $color_gray_5;
        @include font('080');

        @include tablet {
          @include font('090');
        }

        @include desktop {
          @include font('100');
        }
      }

      &.active {
        a, span {
          color: $color_black;
        }
      }
    }
  }

  .locations-footer-title {
    display: block;
    @include font('040');
    padding: 0 $space-050;
    @include stack-space($space-020);

    @include tablet {
      @include font('050');
      @include inset-space(0);
      @include stack-space($space-030);
    }
  }

  .locations-footer-link {
    display: block;
    @include font('070');
    padding: 0 $space-050;

    @include tablet {
      @include inset-space(0);
      @include font('090');
    }
  }

  .theme-dark {
    .locations-list {
      &__item {
        a, span {
          color: $color_gray_3;
        }
        &.active {
          a, span {
            color: $color_white;
          }
        }
      }
    }

    .locations-footer-link {
      color: $color-white;
      }
  }
</style>
